import { type AxialCoord, axialToPixel, hexDistance, hexEquals, hexKey, hexNeighbors } from "./hex";
import { fractalNoise } from "./noise";
import { nextFloat, nextInt, nextUint32, type RngState, type RngStep } from "./rng";
import { orbitOf, type SymmetryName, symmetryStep } from "./symmetry";

export interface MapOptions {
  readonly minHexCount: number;
  readonly maxHexCount: number;
  /** Largest hex distance from the centre; bounds the blob so it fits a phone. */
  readonly maxRadius: number;
  /** Hexes per noise feature: the width of the shape's bays and peninsulas and of the rough ridges. */
  readonly noiseScale: number;
  /** 0 grows a plain round blob (the pre-3.18 map); 1 lets the growth follow the noise ridges only. */
  readonly shapeNoiseWeight: number;
}

export interface GeneratedMap {
  /** Insertion order is part of the determinism contract; never sort. */
  readonly hexes: readonly AxialCoord[];
  readonly rng: RngState;
}

/** The procgen centre and the point every twin is mirrored through. */
export const MAP_CENTRE: AxialCoord = { q: 0, r: 0 };
const NOISE_OCTAVES = 2;

/**
 * The hex opposite `coord` through the map centre: a half turn, the 1v1 map symmetry. Both
 * factions see the same land, so no start is handed a prize the other lacks (the A10 lesson,
 * made structural instead of measured).
 */
export function mirrorCoord(coord: AxialCoord): AxialCoord {
  return { q: -coord.q, r: -coord.r };
}

/** Seeded noise in [0, 1] at a hex, sampled in the pixel plane so the field is not skewed along an axis. */
export function hexNoise(coord: AxialCoord, scale: number, salt: number): number {
  const point = axialToPixel(coord, 1);
  return fractalNoise(point.x / scale, point.y / scale, salt, NOISE_OCTAVES);
}

/** Number of hexes in a disc of the given radius: 3r^2 + 3r + 1. */
function discSize(radius: number): number {
  return 3 * radius * radius + 3 * radius + 1;
}

/**
 * Uniform draw over the odd counts inside the bounds: the centre plus twin pairs. When the
 * bounds hold no odd count (a fixed even size) the size itself is used and one twin is dropped.
 */
function drawTargetCount(rng: RngState, minHexCount: number, maxHexCount: number): RngStep<number> {
  const firstOdd = minHexCount % 2 === 0 ? minHexCount + 1 : minHexCount;
  const lastOdd = maxHexCount % 2 === 0 ? maxHexCount - 1 : maxHexCount;
  if (firstOdd > lastOdd) {
    return { value: minHexCount, state: rng };
  }
  const step = nextInt(rng, (lastOdd - firstOdd) / 2 + 1);
  return { value: firstOdd + 2 * step.value, state: step.state };
}

/**
 * K5's size draw for a map grown in orbits: uniform over the counts in the bounds that are 1 plus a
 * multiple of the group's step (every orbit's size is one), the least such count above the bounds
 * when they hold none. A mirrored group's last orbit may carry the map past it, by less than the
 * group's order.
 */
function drawOrbitCount(
  rng: RngState,
  minHexCount: number,
  maxHexCount: number,
  step: number,
): RngStep<number> {
  const first = minHexCount + ((step - ((minHexCount - 1) % step)) % step);
  if (first > maxHexCount) {
    return { value: first, state: rng };
  }
  const draw = nextInt(rng, Math.floor((maxHexCount - first) / step) + 1);
  return { value: first + step * draw.value, state: draw.state };
}

/**
 * Grows a connected, half-turn symmetric blob from the centre (Increment 3.18). Each step
 * draws one frontier hex, weighted by seeded value noise so the growth follows ridges and
 * leaves bays, then claims its twin through the centre as well: a symmetric connected set
 * stays connected when a hex and its twin are added, so connectivity and fairness both hold
 * by construction. The radius constraint is applied when a cell enters the frontier, so every
 * draw is valid and the loop cannot stall. Never branches on anything outside the inputs.
 *
 * With a `symmetry` (K5, `rules.starts`) each draw claims the drawn hex's whole orbit under that
 * group, every image in the group's order, so the map is the group's images of one sector and
 * still connected: an image of a frontier hex is next to the image of the land, which is the land.
 * Without one it is the half turn above, byte for byte.
 */
export function generateMap(
  rng: RngState,
  options: MapOptions,
  symmetry?: SymmetryName,
): GeneratedMap {
  const { minHexCount, maxHexCount, maxRadius, noiseScale, shapeNoiseWeight } = options;
  if (minHexCount < 1 || maxHexCount < minHexCount) {
    throw new RangeError(`invalid hex count bounds ${minHexCount}..${maxHexCount}`);
  }
  if (discSize(maxRadius) < maxHexCount) {
    throw new RangeError(
      `radius ${maxRadius} holds ${discSize(maxRadius)} hexes, need ${maxHexCount}`,
    );
  }
  if (noiseScale <= 0 || shapeNoiseWeight < 0 || shapeNoiseWeight > 1) {
    throw new RangeError(`invalid shape noise ${noiseScale} / ${shapeNoiseWeight}`);
  }

  let state = rng;
  const countStep =
    symmetry === undefined
      ? drawTargetCount(state, minHexCount, maxHexCount)
      : drawOrbitCount(state, minHexCount, maxHexCount, symmetryStep(symmetry));
  state = countStep.state;
  const targetCount = countStep.value;
  const saltStep = nextUint32(state);
  state = saltStep.state;
  const salt = saltStep.value;

  const hexes: AxialCoord[] = [MAP_CENTRE];
  const claimed = new Set<string>([hexKey(MAP_CENTRE)]);
  const frontier: AxialCoord[] = [];
  const weights = new Map<string, number>();

  // Squaring the noise sharpens the ridges; the constant part keeps every frontier hex reachable.
  const weightOf = (coord: AxialCoord): number =>
    1 - shapeNoiseWeight + shapeNoiseWeight * hexNoise(coord, noiseScale, salt) ** 2;

  const pushFrontier = (coord: AxialCoord): void => {
    for (const neighbour of hexNeighbors(coord)) {
      const key = hexKey(neighbour);
      if (
        !claimed.has(key) &&
        !weights.has(key) &&
        hexDistance(neighbour, MAP_CENTRE) <= maxRadius
      ) {
        weights.set(key, weightOf(neighbour));
        frontier.push(neighbour);
      }
    }
  };
  const removeFromFrontier = (index: number): void => {
    // Swap-remove keeps the pick O(1) and stays deterministic.
    const last = frontier[frontier.length - 1];
    if (last !== undefined) {
      frontier[index] = last;
    }
    frontier.pop();
  };
  const claim = (coord: AxialCoord): void => {
    hexes.push(coord);
    claimed.add(hexKey(coord));
    pushFrontier(coord);
  };
  pushFrontier(MAP_CENTRE);

  while (hexes.length < targetCount) {
    let total = 0;
    for (const coord of frontier) {
      total += weights.get(hexKey(coord)) ?? 0;
    }
    const draw = nextFloat(state);
    state = draw.state;
    let remaining = draw.value * total;
    let pickIndex = frontier.length - 1;
    for (let index = 0; index < frontier.length; index += 1) {
      const coord = frontier[index];
      remaining -= coord ? (weights.get(hexKey(coord)) ?? 0) : 0;
      if (remaining < 0) {
        pickIndex = index;
        break;
      }
    }
    const candidate = frontier[pickIndex];
    if (candidate === undefined) {
      throw new Error("frontier exhausted; radius check should make this unreachable");
    }
    removeFromFrontier(pickIndex);
    claim(candidate);

    if (symmetry !== undefined) {
      for (const image of orbitOf(candidate, symmetry)) {
        const imageKey = hexKey(image);
        if (!claimed.has(imageKey)) {
          const imageIndex = frontier.findIndex((coord) => hexKey(coord) === imageKey);
          if (imageIndex >= 0) {
            removeFromFrontier(imageIndex);
          }
          claim(image);
        }
      }
      continue;
    }
    const twin = mirrorCoord(candidate);
    const twinKey = hexKey(twin);
    if (hexes.length < targetCount && !claimed.has(twinKey) && !hexEquals(twin, candidate)) {
      const twinIndex = frontier.findIndex((coord) => hexKey(coord) === twinKey);
      if (twinIndex >= 0) {
        removeFromFrontier(twinIndex);
      }
      claim(twin);
    }
  }

  return { hexes, rng: state };
}
